import React from 'react';
import { Service } from '../../models/Service';

export function DashboardAppointmentCard({
  appointment,
  onAppointmentClick,
  getAppointmentDetails,
  t,
  variant = 'default' // 'default', 'pending', 'today'
}) {
  const { customerName, serviceName, customerPhone, duration } = getAppointmentDetails(appointment);

  // Variant-specific styling
  const variantStyles = {
    default: {
      container: "bg-white/70 backdrop-blur-xl border-white/20",
      overlay: "from-white/60 via-white/30 to-transparent",
      avatar: "from-pink-200 to-pink-300",
      avatarText: "text-pink-700",
      badge: "from-emerald-400 to-emerald-500 text-white",
      badgeText: t.confirmed || 'מאושר',
      decorLeft: "bg-pink-300",
      decorRight: "bg-blue-300"
    },
    pending: {
      container: "bg-amber-50/70 backdrop-blur-xl border-amber-200/50 border-2",
      overlay: "from-amber-100/40 via-white/30 to-transparent",
      avatar: "from-amber-200 to-amber-300",
      avatarText: "text-amber-800",
      badge: "bg-amber-100 text-amber-800 border border-amber-200",
      badgeText: "ממתין לאישור",
      decorLeft: "bg-amber-400",
      decorRight: "bg-amber-300"
    },
    today: {
      container: "bg-white/70 backdrop-blur-xl border-white/20",
      overlay: "from-white/60 via-white/30 to-transparent",
      avatar: "from-pink-200 to-pink-300",
      avatarText: "text-pink-700",
      badge: "from-emerald-400 to-emerald-500 text-white",
      badgeText: t.confirmed || 'מאושר',
      decorLeft: "bg-pink-300",
      decorRight: "bg-blue-300"
    }
  };

  const style = variantStyles[variant];

  // ---- Helpers: robust date/time resolution (works with various shapes) ----
  const formatDate = (value) => {
    if (!value) return '';
    try {
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleDateString('he-IL', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return String(value);
    }
  };

  const formatTime = () => {
    // Prefer explicit time string if provided, otherwise derive from a Date-like field
    if (appointment.time) return appointment.time;
    const timeSource = appointment.start || appointment.startAt || appointment.datetime || appointment.dateTime || appointment.date || appointment.startDate;
    if (!timeSource) return t.timeNotSet || 'לא נקבע';
    try {
      const d = timeSource instanceof Date ? timeSource : new Date(timeSource);
      return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return t.timeNotSet || 'לא נקבע';
    }
  };

  const resolveDate = (a) => a?.date || a?.dateISO || a?.startDate || a?.start || a?.startAt || a?.datetime || a?.dateTime;
  const resolvedDateRaw = resolveDate(appointment);
  const appointmentDateLabel = resolvedDateRaw ? formatDate(resolvedDateRaw) : '';

  return (
    <div
      className={`relative ${style.container} rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAppointmentClick(appointment);
      }}
      title="Click to edit appointment"
    >
      {/* Glassmorphism overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${style.overlay} rounded-2xl pointer-events-none`}></div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-3">
          {/* Customer Info */}
          <div className="flex items-center space-x-3 flex-1">
            {/* Name & Contact */}
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1 tracking-tight leading-tight">
                {customerName}
              </h3>
              <div className="flex items-center space-x-1 text-gray-600">
                <div className="w-3 h-3 ml-1 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <span className="text-xs font-medium">{customerPhone}</span>
              </div>
            </div>
          </div>

          {/* Status Badge + (Pending) Date Chip */}
          <div className="flex items-center gap-2">
            <div className={`${appointment.status === 'pending_approval'
              ? 'bg-amber-100 text-amber-800 border border-amber-200'
              : appointment.status === 'scheduled'
                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white'
                : appointment.status === 'completed'
                  ? 'bg-gradient-to-r from-green-400 to-green-500 text-white'
                  : appointment.status === 'cancelled'
                    ? 'bg-gradient-to-r from-red-400 to-red-500 text-white'
                    : appointment.status === 'no_show'
                      ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white'
                      : appointment.status === 'no_show_justified'
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-800'
              } px-3 py-1 rounded-full shadow-lg`}>
              <span className="text-xs">
                {appointment.status === 'pending_approval' && 'ממתין'}
                {appointment.status === 'scheduled' && (t.confirmed || 'מתוזמן')}
                {appointment.status === 'completed' && (t.completed || 'הושלם')}
                {appointment.status === 'cancelled' && (t.cancelled || 'בוטל')}
                {appointment.status === 'no_show' && 'לא הגיע'}
                {appointment.status === 'no_show_justified' && 'לא הגיע - מוצדק'}
                {!['pending_approval', 'scheduled', 'completed', 'cancelled', 'no_show', 'no_show_justified'].includes(appointment.status) && appointment.status}
              </span>
            </div>

            {/* Show appointment date chip ONLY for pending variant */}
            {variant === 'pending' && (
              <div className="px-3 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 shadow-sm whitespace-nowrap">
                <span className="text-xs font-semibold">
                  {appointmentDateLabel || t.dateNotSet || 'תאריך לא נקבע'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Appointment Details */}
        <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/40 shadow-inner">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            {/* Service */}
            <div className="col-span-2 md:col-span-3">
              <p className="text-gray-500 font-medium uppercase tracking-wider mb-0.5">{t.service}</p>
              <p className="text-sm font-bold text-gray-900 leading-tight">{serviceName}</p>
            </div>

            {/* Payment Status Badge - hide for no_show_justified and services without payment */}
            {appointment.is_paid && appointment.status !== 'no_show_justified' && !Service.getServicesWithoutPayment().includes(appointment.service_name) && (
              <div className="absolute bottom-1 left-1">
                <div className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-green-200">
                  ✓ שולם
                </div>
              </div>
            )}
            {!appointment.is_paid && appointment.status !== 'pending_approval' && appointment.status !== 'no_show_justified' && !Service.getServicesWithoutPayment().includes(appointment.service_name) && (
              <div className="absolute bottom-1 left-1">
                <div className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-red-200">
                  ✗ טרם שולם
                </div>
              </div>
            )}

            {/* Time */}
            <div>
              <p className="text-gray-500 font-medium uppercase tracking-wider mb-0.5">{t.time}</p>
              <p className="text-lg font-bold text-gray-900 tabular-nums leading-tight">
                {formatTime()}
              </p>
            </div>

            {/* Duration */}
            <div>
              <p className="text-gray-500 font-medium uppercase tracking-wider mb-0.5">{t.duration || 'משך'}</p>
              <p className="text-sm font-bold text-gray-700 leading-tight">{duration} {t.minutes}</p>
            </div>

            {/* Date (only for pending variant, visible next to time/duration) */}
            {variant === 'pending' && (
              <div>
                <p className="text-gray-500 font-medium uppercase tracking-wider mb-0.5">{t.date || 'תאריך'}</p>
                <p className="text-sm font-bold text-gray-900 leading-tight">{appointmentDateLabel || t.dateNotSet || 'לא נקבע'}</p>
              </div>
            )}

            {/* Crew Member */}
            {appointment.crew_member_name && (
              <div>
                <p className="text-gray-500 font-medium uppercase tracking-wider mb-0.5">{t.staff || 'צוות'}</p>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 ml-1 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {appointment.crew_member_name?.[0] || 'ס'}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-blue-600 truncate">
                    {appointment.crew_member_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subtle decorative elements */}
      <div className={`absolute top-3 left-3 w-1.5 h-1.5 ${style.decorLeft} rounded-full opacity-60`}></div>
      <div className={`absolute bottom-3 right-3 w-1 h-1 ${style.decorRight} rounded-full opacity-40`}></div>
    </div>
  );
}
